'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Phone, MessageCircle } from 'lucide-react';

const navLinks = [
  { label: 'Benefits', href: '/' },
  { label: 'Enroll Now', href: '/enrollment-form' },
  { label: 'Admin', href: '/admin-panel' },
];

export default function Topbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  return (
    <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
      {/* Top contact bar */}
      <div className="gradient-hero text-primary-foreground py-1.5 px-4 text-xs flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium tracking-wide">Luvare Collective Pty Ltd</span>
        <div className="flex items-center gap-4">
          <a
            href="tel:0712055570"
            className="flex items-center gap-1 hover:opacity-80 transition-opacity"
          >
            <Phone size={11} />
            <span>0712 055 570</span>
          </a>
          <a
            href="https://wa.me/27658947355"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:opacity-80 transition-opacity"
          >
            <MessageCircle size={11} />
            <span>WhatsApp</span>
          </a>
        </div>
      </div>
      {/* Main nav */}
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <div className="flex flex-col">
            <span className="font-bold text-primary text-base leading-tight tracking-tight">
              Luvare Collective
            </span>
            <span className="text-muted-foreground text-[10px] leading-tight font-medium tracking-wide uppercase">
              Pty Ltd
            </span>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={`nav-${link?.href}`}
                href={link?.href}
                className={`px-4 py-2 rounded-lg text-sm font-600 transition-all duration-150 ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : link?.label === 'Enroll Now' ?'bg-accent text-accent-foreground hover:opacity-90 font-semibold' :'text-foreground hover:bg-secondary hover:text-primary'
                }`}
              >
                {link?.label}
              </Link>
            );
          })}
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-secondary transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle mobile menu"
        >
          {mobileOpen ? (
            <X size={22} className="text-foreground" />
          ) : (
            <Menu size={22} className="text-foreground" />
          )}
        </button>
      </div>
      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-card border-t border-border px-4 py-4 flex flex-col gap-2">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={`mobile-nav-${link?.href}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : link?.label === 'Enroll Now' ?'bg-accent text-accent-foreground' :'text-foreground hover:bg-secondary'
                }`}
              >
                {link?.label}
              </Link>
            );
          })}
          <div className="mt-3 pt-3 border-t border-border flex flex-col gap-2">
            <a
              href="tel:0712055570"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Phone size={14} />
              0712 055 570 / 0724 740 920
            </a>
            <a
              href="https://wa.me/27658947355"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <MessageCircle size={14} />
              WhatsApp: 065 894 7355
            </a>
          </div>
        </div>
      )}
    </header>
  );
}